import React, { Fragment, useCallback, useEffect, useState } from "react";
import AutomationTask from "./Automations/AutomationTask";
import { useAuth } from "hooks/Auth";
import { apiDel, apiGet, messageFromException } from "helpers/Request";
import { transform as taskTransformer } from "schemas/AutomationTask";
import { CubeIcon } from "@heroicons/react/outline";
import { modal } from "components/HeadlessModal";
import DangerButton from "components/Buttons/DangerButton";
import SecondaryButton from "components/Buttons/SecondaryButton";
import { toast } from "react-hot-toast";
import { PlusIcon } from "@heroicons/react/outline";

export default function AutomationPanel(props) {
    const auth = useAuth();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);

    const addTask = () => {
        setTasks([
            ...tasks,
            {
                contentType: "any",
                fromWorkflow: "Any",
                toWorkflow: "Any",
                actions: [],
            },
        ]);
    };

    const removeTask = (uuid) => {
        if (!uuid) {
            setTasks(tasks.slice(0, -1));
            return;
        }

        modal.warning({
            title: "Delete Task",
            children: (
                <p className="text-sm text-gray-800">Delete selected task?</p>
            ),
            buttons: () => (
                <Fragment>
                    <DangerButton onClick={() => onDelete(uuid)}>
                        Delete
                    </DangerButton>
                    <SecondaryButton onClick={modal.dismiss}>
                        Cancel
                    </SecondaryButton>
                </Fragment>
            ),
        });
    };

    const onDelete = (uuid) => {
        modal.update({
            disabled: true,
        });
        const id = toast.loading("Deleting task...");

        apiDel(`/v4/automation-task/${uuid}`, auth.publication)
            .then(() => {
                toast.success("Task deleted", { id });
                modal.dismiss();
                setTasks(tasks.filter((task) => task.uuid !== uuid));
            })
            .catch((err) => {
                modal.dismiss();
                toast.dismiss(id);
                modal.error({
                    title: "Error Deleting Task",
                    children: messageFromException(err),
                });
            });
    };

    const loadTasks = useCallback(() => {
        setLoading(true);
        apiGet("/v4/automation-task", auth.publication)
            .then((resp) => {
                setTasks(taskTransformer(resp));
            })
            .finally(() => {
                setLoading(false);
            });
    }, [setTasks, auth.publication]);

    useEffect(() => {
        loadTasks();
    }, [auth.publication, loadTasks]);

    return (
        <div className="flex h-full w-full flex-col">
            <div className="flex flex-grow flex-row overflow-auto p-4">
                <div className="w-full">
                    <div className="space-y-4">
                        <div>
                            <p className="text-sm text-gray-800">
                                Automatically preform transitions and actions
                                when workflow status change.
                            </p>
                        </div>
                        {loading && (
                            <div className="w-full">
                                <CubeIcon className="mx-auto h-8 w-8 animate-rotate-pulse text-gray-400" />
                            </div>
                        )}
                        {tasks.map((task, index) => (
                            <AutomationTask
                                key={task.uuid || index}
                                task={task}
                                open={task.actions.length === 0}
                                onRemove={removeTask}
                            />
                        ))}

                        <button
                            onClick={addTask}
                            className="w-full rounded-sm border  border-gray-100 p-2 py-2 text-center text-sm text-gray-800 shadow-sm transition-all duration-200 hover:border-gray-300 hover:shadow-lg"
                        >
                            <PlusIcon className="mr-2 inline-block h-4 w-4" />
                            New Task
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
